/** Tailwind-aware class merger; same shape as shadcn's `cn`. */
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * East Africa Time — the operator timezone for TZ/KE/UG (UTC+3, no DST). All
 * admin-portal timestamps are formatted in EAT regardless of the viewer's
 * browser timezone, so what an operator in Dar sees matches server records.
 */
const EAT_TZ = "Africa/Nairobi";

/** Coerce an API timestamp (ISO string / epoch / Date) to a valid Date, or null. */
function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Date + time in EAT, e.g. "02 Jul 2026, 13:15". Returns "—" for empty/invalid. */
export function formatDateTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: EAT_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** Date only in EAT, e.g. "02 Jul 2026". Returns "—" for empty/invalid. */
export function formatDate(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: EAT_TZ,
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Time only in EAT, e.g. "13:15". Returns "—" for empty/invalid. */
export function formatTime(value: string | number | Date | null | undefined): string {
  const d = toDate(value);
  if (!d) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: EAT_TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

/** Format a Money-like row column for display. */
export function formatMoney(amountMinor: number, currency: string): string {
  // TZS is displayed without decimals; other currencies use 2.
  const decimals = currency === "TZS" ? 0 : 2;
  const value = amountMinor / Math.pow(10, decimals);
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
